import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft } from 'react-feather';
import { TabMenu } from '@/UIlibrary/TabMenu/TabMenu';
import { BRAND_COLORS } from '@/UIlibrary/globalTheme';
import { Typography } from '@/UIlibrary/Typography/Typography';
import { useTranslations } from 'use-intl';
import VanCreditQuestionnaire from './VanCreditQuestionnaire';
import Button from '@/UIlibrary/Button/Button';
import ProfileDrawer from './ProfileDrawer';
import {
	Request,
	RequestStatus,
	useGetVanCreditRequestQuery,
	OnboardingStatus
} from '@/graphql/__generated__/van-credits';
import { Registry, useRegistriesLazyQuery } from '@/graphql/__generated__/onboarding';
import { useApolloClient } from '@/providers/MultiApolloProvider';
import { SCHEMAS } from '@/apolloClient';
import RejectModal from './VanCreditRequestDetails/RejectModal';
import { Tooltip } from '@/UIlibrary/Tooltip/Tooltip';
import Banner from '@/UIlibrary/Banner/Banner';
import { useUpdateVanQuestionnaireStore } from '@/stores/useUpdateVanQuestionnaire';
import VanCreditSimulation from './VanCreditSimulation';
import CreditCover from './VanCreditRequestDetails/CreditCover';
import CreditCommitteeRuling from './VanCreditRequestDetails/CreditCommitteeRuling';
import VanCreditContainer from './VanCreditContainer';

const VanCreditRequestDetails: React.FC = () => {
	const t = useTranslations('VanCreditRequestDetails');
	const [activeTab, setActiveTab] = useState(0);
	const [isProfileOpen, setIsProfileOpen] = useState(false);
	const [drawerWidth, setDrawerWidth] = useState(300);
	const [isResizing, setIsResizing] = useState(false);
	const { requestId, lang } = useParams();

	const vanCreditsClient = useApolloClient(SCHEMAS.VAN_CREDITS);
	const { loading, data, error, refetch } = useGetVanCreditRequestQuery({
		client: vanCreditsClient,
		variables: { id: requestId || '' },
		errorPolicy: 'all'
	});

	const [request, setRequest] = useState<Request | null>(null);
	const [registry, setRegistry] = useState<Registry | null | undefined>(null);
	const onboarding = useApolloClient(SCHEMAS.ONBOARDING);

	const [getRegistryInformation] = useRegistriesLazyQuery({
		client: onboarding,
		fetchPolicy: 'network-only',
		nextFetchPolicy: 'cache-first'
	});
	const [isRejectClientModalOpen, setIsRejectClientModalOpen] = useState(false);
	const clientCantBeRejectedStatus = [RequestStatus.REJECTED];

	const canAccessCommitteeTab = [
		RequestStatus.PASSED_TO_CREDIT_COMMITTEE,
		RequestStatus.APPROVED_BY_CREDIT_COMMITTEE,
		RequestStatus.REJECTED_BY_CREDIT_COMMITTEE,
		RequestStatus.SIGNED_DOCUMENTS_UPLOADED
	];
	const canAccessCreditTab = [
		RequestStatus.APPROVED_BY_CREDIT_COMMITTEE,
		RequestStatus.SIGNED_DOCUMENTS_UPLOADED
	];
	const fullName = request
		? `${request.names || ''} ${request.firstLastName || ''} ${request.secondLastName || ''}`.trim()
		: '';

	const tabs = [
		{
			title: t('Questionnaire'),
			content: (
				<VanCreditQuestionnaire requestData={request} refetchData={refetch} registry={registry} />
			),
			badgeVisible: false
		},
		{
			title: t('VanCreditSimulation'),
			content: <VanCreditSimulation requestData={request} refetchData={refetch} />,
			disabled: !request?.callComments,
			disabledTooltip: t('SimulationDisabled')
		},
		{
			title: t('CreditCover'),
			content: <CreditCover requestData={request} refetchData={refetch} registry={registry} />,
			badgeVisible: false,
			disabled:
				!request?.callComments ||
				request?.simulation === null ||
				request?.evaluationMatrix === null ||
				request?.onboardingStatus === OnboardingStatus.REJECTED,
			disabledTooltip:
				request?.onboardingStatus === OnboardingStatus.REJECTED
					? t('OnboardingRejectedDisabled')
					: t('CreditCoverDisabled')
		},
		{
			title: t('CreditCommittee'),
			content: <CreditCommitteeRuling requestData={request} refetchData={refetch} />,
			badgeVisible: false,
			disabled:
				!canAccessCommitteeTab.includes(request?.requestStatus as RequestStatus) ||
				request?.onboardingStatus === OnboardingStatus.REJECTED,
			disabledTooltip:
				request?.onboardingStatus === OnboardingStatus.REJECTED
					? t('OnboardingRejectedDisabled')
					: t('CreditCommitteeDisabled')
		},
		{
			title: t('VanCredit'),
			content: <VanCreditContainer requestData={request} refetchData={refetch} />,
			badgeVisible: false,
			disabled: !canAccessCreditTab.includes(request?.requestStatus as RequestStatus),
			disabledTooltip: t('CreateVanCreditDisabled')
		}
	];

	const handleTabChange = (index: number) => {
		setActiveTab(index);
	};

	const handleClearQuestionnaireChanges = () => {
		const { resetForm } = useUpdateVanQuestionnaireStore.getState();
		resetForm();
	};

	useEffect(() => {
		if (data?.node) {
			setRequest(data.node as Request);
		}
	}, [data]);

	useEffect(() => {
		const callGetRegistryQuery = async () => {
			if (request?.clientId) {
				const { data } = await getRegistryInformation({
					variables: {
						filter: {
							searchTerm: request?.clientId
						},
						first: 1
					}
				});
				setRegistry(
					data?.registries?.edges && data.registries.edges.length > 0
						? data.registries.edges[0]?.node
						: null
				);
			}
		};
		callGetRegistryQuery();
	}, [getRegistryInformation, request?.clientId]);

	useEffect(() => {
		document.body.style.userSelect = isResizing ? 'none' : '';
		return () => {
			document.body.style.userSelect = '';
		};
	}, [isResizing]);

	if (loading) {
		return (
			<div className="flex justify-center items-center h-screen">
				<div className="loader"></div>
			</div>
		);
	}

	if (error) {
		return (
			<div className="flex flex-col justify-center items-center h-screen gap-4 p-4">
				<Typography size="md" color="error">
					{error.message}
				</Typography>
			</div>
		);
	}

	return (
		<div className="flex relative">
			<div
				id="main-content"
				className="flex-1 flex flex-col min-h-0"
				style={{
					marginRight: isProfileOpen ? `${drawerWidth}px` : 0,
					transition: isResizing ? 'none' : 'margin-right 300ms'
				}}
			>
				<div className="sticky top-0 bg-white z-30 print:hidden">
					<div className="flex flex-row items-center justify-between gap-4 min-h-16 p-4">
						<div className="flex items-center gap-2 w-3/4">
							<Link to={`..`} onClick={handleClearQuestionnaireChanges}>
								<ArrowLeft size={24} color={BRAND_COLORS.brand600} />
							</Link>
							<div className="flex flex-col gap-3 justify-left">
								<div className="flex flex-col gap-1">
									<Typography size="sm" type="display">
										{fullName}
									</Typography>
									<div>
										<Button
											label={t('ViewProfile')}
											onClick={() => setIsProfileOpen(true)}
											size="small"
											textOnly
										/>
									</div>
								</div>
								<Typography size="sm" color="subtitle">
									{t('RequestID')}: {request?.requestId || ''}
								</Typography>
								{request?.requestStatus === RequestStatus.REJECTED && (
									<Banner
										message={t('RejectReasonLabel')}
										secondaryMessage={request?.rejectionReason || ''}
										variant="error"
									/>
								)}
							</div>
						</div>
						<div className="flex items-center gap-4">
							<Tooltip
								content={
									clientCantBeRejectedStatus.includes(request?.requestStatus as RequestStatus)
										? t('RejectDisabledTooltip')
										: ''
								}
								id="reject-tooltip"
							>
								<Button
									label={t('RejectVanCredit')}
									onClick={() => setIsRejectClientModalOpen(true)}
									size="auto"
									color="error"
									disabled={clientCantBeRejectedStatus.includes(
										request?.requestStatus as RequestStatus
									)}
								/>
							</Tooltip>
						</div>
					</div>
					<hr />
				</div>

				<div className="flex-1 overflow-y-auto min-h-0 max-h-[calc(78vh)]">
					<div className="w-full p-4">
						<TabMenu
							activeTabIndex={activeTab}
							tabs={tabs}
							maxWidth={1500}
							onTabChanged={handleTabChange}
						/>
					</div>
				</div>
			</div>

			<ProfileDrawer
				isOpen={isProfileOpen}
				onClose={() => setIsProfileOpen(false)}
				vehiclesRequested={request?.creditDetails?.vehiclesRequested || ''}
				clientId={request?.clientId || ''}
				fullName={fullName}
				email={request?.email || ''}
				phoneNumber={request?.phoneNumber || ''}
				deliberation={request?.onboardingRegistryDeliberation || ''}
				registry={registry}
				t={t}
				lang={lang || 'es'}
				drawerWidth={drawerWidth}
				onDrawerWidthChange={setDrawerWidth}
				isResizing={isResizing}
				onResizingChange={setIsResizing}
			/>
			<RejectModal
				isOpen={isRejectClientModalOpen}
				onClose={() => setIsRejectClientModalOpen(false)}
				onSuccess={async () => {
					await refetch();
				}}
				vanCreditRequestId={request?.requestId || ''}
			/>
		</div>
	);
};

export default VanCreditRequestDetails;
